import { useState, useEffect, useRef } from "react";
import { Panel } from "primereact/panel";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { getFiltrosReporteCotizacion, getReporteCotizacion } from "../../service/AffariService.js";
import Filtros from "./components/Filtros.js";
import Tabla from "./components/Tabla.js";
import LoaderTransparent from "../../components/ui/LoaderTransparent.js";

const defaultFilters = {
    fechaDesde: null,
    fechaHasta: null,
    folioDesde: "",
    folioHasta: "",
    clienteCodigo: "",
    clienteRut: "",
    oficina: null,
    listaPrecios: null,
    vendedor: null,
    canal: null,
    origen: null,
    estado: null,
    visualizacion: "ultimas",
    historia: false
};

const ReportePage = ({ titulo, variant }) => {
    const [filterOptions, setFilterOptions] = useState(null);
    const [filters, setFilters] = useState(defaultFilters);
    const [causas, setCausas] = useState([]);
    const [competencias, setCompetencias] = useState([]);
    const [cotizaciones, setCotizaciones] = useState([]);
    const [estados, setEstados] = useState([]);
    const [glosacausas, setGlosacausas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [collapsedFiltro, setCollapsedFiltro] = useState(false);
    const [isMobile, setIsMobile] = useState(
        () => (typeof window !== "undefined" ? window.innerWidth < 768 : false)
    );
    const toast = useRef(null);

    useEffect(() => {
        getFiltrosReporteCotizacion().then((data) => {
            setFilterOptions(data);

            if (data) {
                const parseDate = (dateStr) => {
                    if (!dateStr) return null;
                    const [day, month, year] = dateStr.split("/");
                    return new Date(year, month - 1, day);
                };

                setFilters((prev) => ({
                    ...prev,
                    fechaDesde: data.fechaDesde ? parseDate(data.fechaDesde) : prev.fechaDesde,
                    fechaHasta: data.fechaHasta ? parseDate(data.fechaHasta) : prev.fechaHasta
                }));
            }
        });
    }, []);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleSearch = async (currentFilters) => {
        setLoading(true);

        const params = new URLSearchParams();

        const formatDate = (date) => {
            if (!date) return "";
            const d = new Date(date);
            const day = String(d.getDate()).padStart(2, "0");
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const year = d.getFullYear();
            return `${year}-${month}-${day}`;
        };

        const appendArrayParams = (key, items) => {
            if (items && Array.isArray(items)) {
                items.forEach((item) => {
                    let value = item;
                    if (item && typeof item === "object") {
                        if (item.codigo !== undefined) {
                            value = item.codigo;
                        } else if (item.id !== undefined) {
                            value = item.id;
                        } else if (item.value !== undefined) {
                            value = item.value;
                        } else if (item.descripcion !== undefined) {
                            value = item.descripcion;
                        }
                    }
                    if (value !== undefined && value !== null && value !== "") {
                        params.append(`${key}[]`, value);
                    }
                });
            }
        };

        if (currentFilters.folioDesde) params.append("folioDesde", currentFilters.folioDesde);
        if (currentFilters.folioHasta) params.append("folioHasta", currentFilters.folioHasta);
        if (currentFilters.clienteCodigo) params.append("codigo", currentFilters.clienteCodigo);
        if (currentFilters.clienteRut) params.append("rut", currentFilters.clienteRut);

        appendArrayParams("zona", currentFilters.oficina);
        appendArrayParams("listaPrecio", currentFilters.listaPrecios);
        appendArrayParams("vendedor", currentFilters.vendedor);
        appendArrayParams("canal", currentFilters.canal);
        appendArrayParams("origen", currentFilters.origen);
        appendArrayParams("estado", currentFilters.estado);

        const ordenMap = { todas: "T", primeras: "P", ultimas: "U" };
        if (currentFilters.visualizacion) {
            params.append("orden", ordenMap[currentFilters.visualizacion] || "U");
        }

        params.append("historia", currentFilters.historia ? "true" : "false");
        params.append("tipo", variant);

        let queryString = params.toString();
        if (currentFilters.fechaDesde) {
            queryString += `&fechaDesde=${formatDate(currentFilters.fechaDesde)}`;
        }
        if (currentFilters.fechaHasta) {
            queryString += `&fechaHasta=${formatDate(currentFilters.fechaHasta)}`;
        }

        try {
            const response = await getReporteCotizacion(queryString);

            setCausas(response.causas);
            setCompetencias(response.competencias);
            setCotizaciones(response.cotizaciones);
            setEstados(response.estados);
            setGlosacausas(response.glosaCausas);

            setCollapsedFiltro(true);
        } catch (error) {
            console.error("Error fetching reporte:", error);
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: "Error al obtener el reporte"
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-2">
            <Toast ref={toast} position="top-right" appendTo={document.body} baseZIndex={4000} />

            <div
                className={`flex flex-column ${isMobile ? "" : "overflow-hidden"}`}
                style={isMobile ? {} : { height: "calc(100vh - 6rem)" }}
            >
                <div className="p-fluid flex-none">
            
                    <Card className="bg-blue-50 border-bottom-1 border-blue-200 py-2 px-3 mb-2">
                        <h3 className="m-0 text-blue-700">{titulo}</h3>
                    </Card>
                    
                    <Panel
                        header={
                            <div
                                className="flex align-items-center cursor-pointer"
                                onClick={() => setCollapsedFiltro((prev) => !prev)}
                            >
                                <div className="surface-100 border-circle w-2rem h-2rem flex align-items-center justify-content-center mr-3">
                                    <i className="pi pi-list text-primary text-sm" />
                                </div>
                                <span className="text-xl text-900">Filtros</span>
                            </div>
                        }
                        toggleable
                        collapsed={collapsedFiltro}
                        onToggle={(e) => setCollapsedFiltro(e.value)}
                        className="border-none shadow-1 border-round-xl overflow-hidden surface-card"
                    >
                        <div className="p-2">
                            <Filtros
                                data={filterOptions}
                                onSearch={handleSearch}
                                loading={loading}
                                filters={filters}
                                setFilters={setFilters}
                                variant={variant}
                            />
                        </div>
                    </Panel>
                </div>

                <div className={`p-fluid mt-2 ${isMobile ? "" : "flex-1 overflow-hidden"}`}>
                    <Panel className="border-none shadow-1 border-round-xl overflow-hidden surface-card panel-full-height">
                        <div className="h-full flex flex-column p-2" style={{ position: "relative" }}>
                            {loading && (
                                <LoaderTransparent coverParent overlayTint="transparent" />
                            )}
                            <Tabla
                                setLoading={setLoading}
                                cotizaciones={cotizaciones}
                                estados={estados}
                                glosacausas={glosacausas}
                                competencias={competencias}
                                causas={causas}
                                toast={toast}
                                onRefresh={() => handleSearch(filters)}
                                variant={variant}
                            />
                        </div>
                    </Panel>
                </div>
            </div>
        </div>
    );
};

export default ReportePage;
